import { formatDateForApi } from '@/utils/datePresentation';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import {
  ALL_OPTION_VALUE,
  buildSelectionLabel,
  createAllOption,
} from '@/utils/filterSelectors';

export const createToolbarAllOption = (allOptionLabels = {}, key = '') => {
  const customLabel = String(allOptionLabels?.[key] || '').trim();
  if (customLabel !== '') {
    return { value: ALL_OPTION_VALUE, label: customLabel };
  }

  return createAllOption();
};

export const normalizeToolbarDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === 'string') {
    const raw = value.trim();
    if (raw === '') {
      return null;
    }

    const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const year = Number(dateOnlyMatch[1]);
      const month = Number(dateOnlyMatch[2]);
      const day = Number(dateOnlyMatch[3]);
      const parsedDateOnly = new Date(year, month - 1, day);

      if (!Number.isNaN(parsedDateOnly.getTime())) {
        return new Date(parsedDateOnly.getFullYear(), parsedDateOnly.getMonth(), parsedDateOnly.getDate());
      }
    }

    const slashParts = raw.split('/').map((part) => part.trim());
    if (slashParts.length === 3) {
      const day = Number(slashParts[0]);
      const month = Number(slashParts[1]);
      const year = Number(slashParts[2]);

      if (Number.isInteger(day) && Number.isInteger(month) && Number.isInteger(year)) {
        const parsedSlashDate = new Date(year, month - 1, day);
        if (!Number.isNaN(parsedSlashDate.getTime())) {
          return new Date(parsedSlashDate.getFullYear(), parsedSlashDate.getMonth(), parsedSlashDate.getDate());
        }
      }
    }

    const parsedDate = new Date(raw);
    if (!Number.isNaN(parsedDate.getTime())) {
      return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
    }
  }

  return null;
};

export const extractToolbarDateRange = (dateRangeValue, { startKey = 'start', endKey = 'end' } = {}) => {
  const range = Array.isArray(dateRangeValue) ? dateRangeValue : [];
  const start = normalizeToolbarDate(range[0]);
  const end = normalizeToolbarDate(range[1]);

  return {
    [startKey]: start ? formatDateForApi(start) : '',
    [endKey]: end ? formatDateForApi(end) : '',
  };
};

export const buildToolbarSelectionLabel = (values, customAllLabel = '') => {
  const label = String(customAllLabel || '').trim();
  if (label !== '') {
    return label;
  }

  return buildSelectionLabel(values);
};

export const mapToolbarOptions = (items) => {
  return normalizeCatalogOptions(Array.isArray(items) ? items : [], { useCodeInLabel: false });
};

export const resolveToolbarToggleState = (showFilters) => {
  if (showFilters) {
    return {
      showFilters: false,
      showDateInput: null,
    };
  }

  return {
    showFilters: true,
    showDateInput: true,
  };
};

export const shouldEmitToolbarDateChange = (payload, dateRangeValue) => {
  if (payload?.source === 'clear') {
    return true;
  }

  const range = Array.isArray(payload?.value)
    ? payload.value
    : (Array.isArray(dateRangeValue) ? dateRangeValue : []);

  const start = normalizeToolbarDate(range[0]);
  const end = normalizeToolbarDate(range[1]);

  return Boolean(payload?.isComplete || (start && end));
};

export const buildToolbarApplyPayload = (search, filters) => {
  return {
    search: String(search || ''),
    filters,
  };
};
